import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { mapStyleService } from '../map-engine';
import type { SystemMapRouteGeometry, SystemMapStopSummary, UrbanNetworkEdge } from './api';
import {
  useCandidateAssociationDashedLink,
  useCandidateEdgeHighlight,
  useCurrentAssociationDashedLink,
  useDraftAssociationDashedLink,
  useDraftMarkerLayer,
  useFlyOnFocus,
  useMapClickForPlacement,
  URBAN_NETWORK_LAYER,
  useUrbanNetworkLayer,
} from './systemMapEdgeLayers';

const BAHIA_CENTER: [number, number] = [-62.27, -38.73];
const BAHIA_ZOOM = 11;

const ROUTES_SOURCE = 'smap-routes-source';
const ROUTES_LAYER = 'smap-routes-layer';
const STOPS_SOURCE = 'smap-stops-source';
const STOPS_LAYER = 'smap-stops-layer';
const EDGE_SOURCE = 'smap-edge-source';
const EDGE_LAYER = 'smap-edge-layer';

// Paleta categórica de 12 colores — se cicla para las 21 rutas (no hace falta que cada una sea única, alcanza con distinguibilidad razonable + resaltado/atenuado al seleccionar).
const ROUTE_PALETTE = [
  '#56d7ff',
  '#ff9f56',
  '#7de3a0',
  '#c792ea',
  '#ffd166',
  '#ef476f',
  '#06d6a0',
  '#a78bfa',
  '#f78c6b',
  '#4cc9f0',
  '#f9c74f',
  '#90be6d',
];

const STATUS_COLORS: Record<string, string> = {
  valid: '#7de3a0',
  needs_review: '#ffb84d',
  orphaned: '#ff6b6b',
};
const STATUS_OTHER_COLOR = '#9fb0c2';

function routeColor(analysisId: string, allIds: string[]): string {
  const idx = allIds.indexOf(analysisId);
  return ROUTE_PALETTE[idx % ROUTE_PALETTE.length];
}

// El tipado de `match` en @types/maplibre-gl exige una tupla de longitud
// fija — no modela un spread dinámico de N pares id/color. Mismo patrón
// que ya usa `buildPopColorExpr` en VsjMapSection.tsx para su expresión de
// color dinámica.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildRouteColorExpression(allRouteIds: string[]): any {
  return [
    'match',
    ['get', 'analysisId'],
    ...allRouteIds.flatMap(id => [id, routeColor(id, allRouteIds)]),
    '#56d7ff',
  ];
}

function buildRoutesFeatureCollection(
  routes: SystemMapRouteGeometry[]
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const route of routes) {
    for (const f of route.ida) {
      features.push({ ...f, properties: { analysisId: route.analysisId, label: route.label } });
    }
    if (route.vuelta) {
      for (const f of route.vuelta) {
        features.push({ ...f, properties: { analysisId: route.analysisId, label: route.label } });
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

function buildStopsFeatureCollection(stops: SystemMapStopSummary[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = stops
    .filter(s => s.lon !== null && s.lat !== null)
    .map(s => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lon as number, s.lat as number] },
      properties: {
        id: s.id,
        status: s.status ?? 'other',
        name: s.name ?? '',
        municipalStopId: s.municipalStopId ?? '',
      },
    }));
  return { type: 'FeatureCollection', features };
}

function useMapInit(containerRef: RefObject<HTMLDivElement>): {
  mapRef: RefObject<maplibregl.Map | null>;
  mapReady: boolean;
} {
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: mapStyleService.getDefaultStyle(),
      center: BAHIA_CENTER,
      zoom: BAHIA_ZOOM,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.on('load', () => setMapReady(true));
    return (): void => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- containerRef es un ref estable, no una dependencia real
  }, []);

  return { mapRef, mapReady };
}

function useRoutesLayer(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  routesGeoJson: GeoJSON.FeatureCollection
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!map.getSource(ROUTES_SOURCE)) {
      map.addSource(ROUTES_SOURCE, { type: 'geojson', data: routesGeoJson });
      map.addLayer({
        id: ROUTES_LAYER,
        type: 'line',
        source: ROUTES_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-width': 2, 'line-color': '#56d7ff', 'line-opacity': 0.75 },
      });
    } else {
      (map.getSource(ROUTES_SOURCE) as maplibregl.GeoJSONSource).setData(routesGeoJson);
    }
  }, [mapRef, mapReady, routesGeoJson]);
}

function useRouteHighlight(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  selectedLineId: string | null,
  allRouteIds: string[]
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer(ROUTES_LAYER)) return;

    const colorExpr = buildRouteColorExpression(allRouteIds);
    const widthExpr: maplibregl.ExpressionSpecification | number = selectedLineId
      ? ['case', ['==', ['get', 'analysisId'], selectedLineId], 4, 1.2]
      : 2;
    const opacityExpr: maplibregl.ExpressionSpecification | number = selectedLineId
      ? ['case', ['==', ['get', 'analysisId'], selectedLineId], 1, 0.15]
      : 0.75;

    map.setPaintProperty(ROUTES_LAYER, 'line-color', colorExpr);
    map.setPaintProperty(ROUTES_LAYER, 'line-width', widthExpr);
    map.setPaintProperty(ROUTES_LAYER, 'line-opacity', opacityExpr);
  }, [mapRef, mapReady, selectedLineId, allRouteIds]);
}

function useStopsLayer(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  stopsGeoJson: GeoJSON.FeatureCollection,
  onSelectStop: (stopId: string) => void
): void {
  // El listener de click se registra una sola vez (al crear la capa) — un
  // ref evita que quede atado a una versión vieja de `onSelectStop` si el
  // padre le pasa una función nueva en cada render.
  const onSelectStopRef = useRef(onSelectStop);
  onSelectStopRef.current = onSelectStop;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const colorExpr: maplibregl.ExpressionSpecification = [
      'match',
      ['get', 'status'],
      'valid',
      STATUS_COLORS.valid,
      'needs_review',
      STATUS_COLORS.needs_review,
      'orphaned',
      STATUS_COLORS.orphaned,
      STATUS_OTHER_COLOR,
    ];

    if (!map.getSource(STOPS_SOURCE)) {
      map.addSource(STOPS_SOURCE, { type: 'geojson', data: stopsGeoJson });
      map.addLayer({
        id: STOPS_LAYER,
        type: 'circle',
        source: STOPS_SOURCE,
        paint: {
          'circle-radius': 4,
          'circle-color': colorExpr,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#06131f',
        },
      });
      map.on('click', STOPS_LAYER, e => {
        const id = e.features?.[0]?.properties?.id as string | undefined;
        if (id) onSelectStopRef.current(id);
      });
      map.on('mouseenter', STOPS_LAYER, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', STOPS_LAYER, () => {
        map.getCanvas().style.cursor = '';
      });
    } else {
      (map.getSource(STOPS_SOURCE) as maplibregl.GeoJSONSource).setData(stopsGeoJson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSelectStop cambia de identidad en cada render del padre; el listener se registra una sola vez y lee el valor más reciente vía closure sobre el prop porque React re-renderiza este hook completo cuando cambian mapReady/stopsGeoJson
  }, [mapRef, mapReady, stopsGeoJson]);
}

function useStopHighlight(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  selectedStopId: string | null
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer(STOPS_LAYER)) return;
    const radiusExpr: maplibregl.ExpressionSpecification | number = selectedStopId
      ? ['case', ['==', ['get', 'id'], selectedStopId], 8, 4]
      : 4;
    const strokeWidthExpr: maplibregl.ExpressionSpecification | number = selectedStopId
      ? ['case', ['==', ['get', 'id'], selectedStopId], 2.5, 1]
      : 1;
    map.setPaintProperty(STOPS_LAYER, 'circle-radius', radiusExpr);
    map.setPaintProperty(STOPS_LAYER, 'circle-stroke-width', strokeWidthExpr);
  }, [mapRef, mapReady, selectedStopId]);
}

/** Anillo celeste para paradas "confirmadas en esta sesión" (`useSessionOverrides`) — NUNCA cambia el color por status real (eso seguiría siendo el QA real), solo el trazo, para que la simulación de sesión sea visible en el mapa sin poder confundirse con el color de estado real. */
function useSessionOverrideStroke(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  overrideIds: string[]
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer(STOPS_LAYER)) return;
    const strokeColorExpr: maplibregl.ExpressionSpecification | string =
      overrideIds.length > 0
        ? ['case', ['in', ['get', 'id'], ['literal', overrideIds]], '#22d3ee', '#06131f']
        : '#06131f';
    map.setPaintProperty(STOPS_LAYER, 'circle-stroke-color', strokeColorExpr);
  }, [mapRef, mapReady, overrideIds]);
}

function useEdgeHighlight(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  edgeGeometry: GeoJSON.LineString | GeoJSON.MultiLineString | null
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (map.getLayer(EDGE_LAYER)) map.removeLayer(EDGE_LAYER);
    if (map.getSource(EDGE_SOURCE)) map.removeSource(EDGE_SOURCE);

    if (!edgeGeometry) return;

    map.addSource(EDGE_SOURCE, {
      type: 'geojson',
      data: { type: 'Feature', geometry: edgeGeometry, properties: {} },
    });
    map.addLayer({
      id: EDGE_LAYER,
      type: 'line',
      source: EDGE_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-width': 5, 'line-color': '#ffe066', 'line-opacity': 0.85 },
    });

    return (): void => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(EDGE_LAYER)) m.removeLayer(EDGE_LAYER);
        if (m.getSource(EDGE_SOURCE)) m.removeSource(EDGE_SOURCE);
      } catch {
        /* mapa ya destruido */
      }
    };
  }, [mapRef, mapReady, edgeGeometry]);
}

/**
 * Mapa MapLibre del sistema — 21 rutas cerradas + ~1735 paradas, READ ONLY
 * absoluto: nunca permite arrastrar una parada, seleccionar un edge para
 * guardar, ni ningún botón de aplicar/reasignar. Cada capa vive en su
 * propio hook (`useRoutesLayer`/`useStopsLayer`/etc.) para mantener este
 * componente corto — la lógica de cada capa es independiente.
 */
export function SystemMapCanvas({
  routes,
  stops,
  selectedLineId,
  selectedStopId,
  stopPoint,
  edgeGeometry,
  projectedPoint,
  urbanNetworkEnabled,
  urbanNetworkEdges,
  candidateEdgeGeometry,
  reviewFocusToken,
  draftModeArmed,
  draftPoint,
  draftEdgeGeometry,
  sessionOverrideIds,
  onSelectStop,
  onSelectEdge,
  onPlaceDraft,
}: {
  routes: SystemMapRouteGeometry[];
  stops: SystemMapStopSummary[];
  selectedLineId: string | null;
  selectedStopId: string | null;
  /** Coordenadas de la parada seleccionada — null si no hay ninguna. */
  stopPoint: [number, number] | null;
  edgeGeometry: GeoJSON.LineString | GeoJSON.MultiLineString | null;
  /** Punto proyectado REAL persistido del edge actual — para la línea punteada. */
  projectedPoint: [number, number] | null;
  urbanNetworkEnabled: boolean;
  urbanNetworkEdges: UrbanNetworkEdge[] | null;
  candidateEdgeGeometry: GeoJSON.LineString | null;
  /** Se incrementa solo en Anterior/Siguiente/Iniciar de "Revisar pendientes" — dispara el auto-zoom. */
  reviewFocusToken: number;
  /** true mientras "Nueva parada" está armado esperando el próximo click en el mapa vacío. */
  draftModeArmed: boolean;
  draftPoint: [number, number] | null;
  draftEdgeGeometry: GeoJSON.LineString | null;
  /** Ids con estado "confirmado en esta sesión" — solo para el trazo del punto, ver `useSessionOverrideStroke`. */
  sessionOverrideIds: string[];
  onSelectStop: (stopId: string) => void;
  onSelectEdge: (edgeId: string) => void;
  onPlaceDraft: (lon: number, lat: number) => void;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mapRef, mapReady } = useMapInit(containerRef);

  const allRouteIds = useMemo(() => routes.map(r => r.analysisId), [routes]);
  const routesGeoJson = useMemo(() => buildRoutesFeatureCollection(routes), [routes]);
  const stopsGeoJson = useMemo(() => buildStopsFeatureCollection(stops), [stops]);

  useRoutesLayer(mapRef, mapReady, routesGeoJson);
  useUrbanNetworkLayer(mapRef, mapReady, urbanNetworkEnabled, urbanNetworkEdges, ROUTES_LAYER, onSelectEdge);
  useRouteHighlight(mapRef, mapReady, selectedLineId, allRouteIds);
  useStopsLayer(mapRef, mapReady, stopsGeoJson, onSelectStop);
  useStopHighlight(mapRef, mapReady, selectedStopId);
  useSessionOverrideStroke(mapRef, mapReady, sessionOverrideIds);
  useEdgeHighlight(mapRef, mapReady, edgeGeometry);
  useCandidateEdgeHighlight(mapRef, mapReady, candidateEdgeGeometry);
  useCurrentAssociationDashedLink(mapRef, mapReady, stopPoint, projectedPoint);
  useCandidateAssociationDashedLink(mapRef, mapReady, stopPoint, candidateEdgeGeometry);
  useFlyOnFocus(mapRef, mapReady, stopPoint, reviewFocusToken);
  useDraftMarkerLayer(mapRef, mapReady, draftPoint);
  useDraftAssociationDashedLink(mapRef, mapReady, draftPoint, draftEdgeGeometry);
  useMapClickForPlacement(mapRef, mapReady, draftModeArmed, [STOPS_LAYER, URBAN_NETWORK_LAYER], onPlaceDraft);

  return <div ref={containerRef} className="smap-canvas" />;
}
